import React, { ReactElement } from 'react';

import { TFunction } from 'i18next';

import WordsLimitV3ExplanationText from '~/modules/tools/components/WordsLimits/variants/WordsLimitV3/ExplanationText';
import analytics from '~/services/analytics';

import { HIGHLIGHT, HighLightConfigType } from '../highlight/constants';

export const ToolsSidebarKey = 'tools';

export const ToolsGalleryPath = '/tools';

export const getToolsGalleryTitle = (t: TFunction): string =>
  t('general:tools', 'Tools');

export const AdvancedArticleGeneratorPath = '/tools/advanced_article_generator';

export const getAdvancedArticleGeneratorTitle = (t: TFunction): string =>
  t('tools:title.long_article_generator', 'Long Article (1000+ Words)');

export const PaperWriterPath = '/tools/paper_writer';

export const getPaperWriterTitle = (t: TFunction): string =>
  t('tools:title.paper_writer', 'Paper Writer');

export const PlagiarismCheckerPath = '/tools/plagiarism_checker';

export const getPlagiarismCheckerTitle = (t: TFunction): string =>
  t('tools:title.plagiarism_checker', 'Plagiarism Checker');

export const AiDetectorPath = '/tools/ai_content_detector';

export const getAiDetectorTitle = (t: TFunction): string =>
  t('tools:title.ai_content_detector', 'AI Content Detector');

export const AiHumanizerPath = '/tools/ai_text_humanizer';

export const getAiHumanizerTitle = (t: TFunction): string =>
  t('tools:title.ai_humanizer', 'AI Text Humanizer');

export const ParaphraseTextPath = '/tools/paraphrase_text';

export const getParaphraseTextTitle = (t: TFunction): string =>
  t('tools:title.paraphrase_text', 'Paraphrase Text');

export const SummariseTextPath = '/tools/summarize_text';

export const getSummariseTextTitle = (t: TFunction): string =>
  t('tools:title.summarise_text', 'Summarise Text');

export const RewriteArticlePath = '/tools/rewrite_article';

export const getRewriteArticleTitle = (t: TFunction): string =>
  t('tools:title.rewrite_article', 'Rewrite Article');

export const AiResearcherPath = '/ai_researcher';

export const AiResearcherSidebarKey = 'ai_researcher';

export const FactCheckerPath = '/tools/fact_checker';

export const getFactCheckerTitle = (t: TFunction): string =>
  t('tools:title.fact_checker', 'Fact Checker');

export const QuizGeneratorPath = '/tools/quiz_generator';

export const getQuizGeneratorTittle = (t: TFunction): string =>
  t('tools:title.quiz_generator', 'Quiz Generator');

export const ToolPath = '/tools/:slug';

export const getToolTitle = (t: TFunction): string =>
  t('tools:tools_item', 'Tools item');

export const validExtensions = ['docx', 'txt', 'pdf'];
export const MAX_FILE_SIZE = 10000000; //10MB

export const DEFAULT_PROMPT_IMPROVES_LIMITS = 5;
export const MAX_PROMPT_IMPROVES_LIMITS = 20;

export const GRAMMAR_CHECK_TOOL_ID = '3a72d3d8-77e0-423e-9cf6-de3b346e0235';
export const AI_RESEARCHER_TOOL_ID = 'f6d33993-1bee-45ab-814f-e298e8b7fd58';
export const CITATION_MACHINE_TOOL_ID = '289c2a3c-2fd8-4714-bc81-635215ec6613';
export const SUMMARIZE_TOOL_ID = '8928337a-ca77-4fd2-a161-b5572b508fa6';
export const PARAPHRASE_TOOL_ID = '8928337a-ca77-4fd2-a161-b5572b508fa5';
export const QUIZ_GENERATOR_TOOL_ID = 'a724acef-8aa7-4ec0-8bb0-c57c9d8076ab';
export const EXTENSION_TOOL_ID = '1230403a-5646-4d39-bff8-af8f92e60d04';
export const AI_DETECTOR_TOOL_ID = 'e00dab29-a1b8-416c-ba4e-fc5bb20a2431';
export const AI_HUMANIZER_TOOL_ID = '4656b8a0-d52f-4c08-a4fe-5d2b83b870c0';
export const PLAGIARISM_TOOL_ID = '725f820a-5dc4-47c7-a7a5-ddb09cf89c1a';

export const getFormSubmitButtonTextConfig = (
  t: TFunction,
): Record<string, string | ReactElement> => ({
  [GRAMMAR_CHECK_TOOL_ID]: t('general:check', 'Check'),
});

export const WIDE_FORM_REQUEST_TOOLS = [GRAMMAR_CHECK_TOOL_ID];

export const IsToolLimitHighLightShowed = 'is_tool_limit_highlight_tutorial';
export const IsToolLimitV2HighLightShowed =
  'is_tool_limit_v2_highlight_tutorial';

export const getToolsCommonHighlightConfig = (
  t: TFunction,
): Record<
  | HIGHLIGHT.TOOLS_OUTPUT_COUNTER
  | HIGHLIGHT.DETECTORS_MONTHLY_LIMIT
  | HIGHLIGHT.DETECTORS_MONTHLY_LIMIT_V2
  | HIGHLIGHT.DETECTORS_MONTHLY_LIMIT_V3,
  HighLightConfigType
> => {
  return {
    [HIGHLIGHT.TOOLS_OUTPUT_COUNTER]: {
      title: t(
        'highlight:tools_output_counter.title',
        'Need more results for one input?',
      ),
      text: t(
        'highlight:tools_output_counter.text',
        'Receive up to 5 response variations for your one request.',
      ),
      onShowCallBack: (): void => {
        analytics.trackEvent('navigation - highlight appear', {
          scenario: 'Outputs - regeneration',
        });
      },
      onSubmitCallback: (updateUserExtraFields): void => {
        analytics.trackEvent('navigation - highlight click', {
          scenario: 'Outputs - regeneration',
        });
        updateUserExtraFields({
          highlights: {
            [HIGHLIGHT.TOOLS_OUTPUT_COUNTER]: 1,
          },
        });
      },
    },
    [HIGHLIGHT.DETECTORS_MONTHLY_LIMIT]: {
      title: t('highlight:detectors.limit.title', 'Monthly Word Balance'),
      text: t(
        'highlight:detectors.limit.text_v1',
        'Amount of words available for you to run through AI Content Detector, AI Text Humanizer, and Plagiarism Checker. The balance refills each month. Unlimited on Annual Plan',
      ),
      buttonText: t('general:next', 'Next'),
    },
    [HIGHLIGHT.DETECTORS_MONTHLY_LIMIT_V2]: {
      title: t('highlight:detectors.limit_v2.title', 'Extra Words Are Added'),
      text: <WordsLimitV3ExplanationText />,
    },
    [HIGHLIGHT.DETECTORS_MONTHLY_LIMIT_V3]: {
      title: t('general:word_balance', 'Word Balance'),
      text: t(
        'highlight:detectors.limit.text_v1',
        'Amount of words available for you to run through AI Content Detector, AI Text Humanizer, and Plagiarism Checker. The balance refills each month. Unlimited on Annual Plan',
      ),
      buttonText: t('general:got_it', 'Got it'),
      onSubmitCallback: (updateUserExtraFields): void => {
        updateUserExtraFields({
          highlights: { [IsToolLimitV2HighLightShowed]: true },
        });
      },
    },
  };
};

export const RewriteArticleTitle = 'Rewrite Article';
export const SummarizeTextTitle = 'Summarize Text';
